import { useEffect, useRef, useCallback } from 'react';

// ── Music constants ──────────────────────────────────────────────────────────
// 80 BPM → 32 beats = exactly 24 s, matching the 6 auto-play slides perfectly.
// Music starts on slide 0 and fades out as the CTA (last) slide appears.
const BPM = 80;
const B = 60 / BPM; // one beat ≈ 0.75 s

// Note frequencies (Hz)
const F = {
  C3: 130.81, G3: 196.00, A3: 220.00,
  C4: 261.63, D4: 293.66, E4: 329.63, G4: 392.00, A4: 440.00,
  C5: 523.25, D5: 587.33, E5: 659.25,
};

// ── Melody — 32 beats, C-major pentatonic ───────────────────────────────────
// Phrase 1 (bars 1-4): ascending warmth → gentle resolve
// Phrase 2 (bars 5-8): high descend (emotional peak) → grand resolution
// null = rest
const MELODY = [
  [F.G4,1],[F.A4,1],[F.C5,1],[F.A4,1],  // ↑ ascending warmth
  [F.G4,1],[F.E4,1],[F.D4,1],[F.E4,1],  // ↓ gentle resolve
  [F.G4,1],[F.A4,1],[F.C5,1],[F.A4,1],  // ↑ repeat (recognition)
  [F.G4,2],[F.E4,1],[null,   1],         //   hold + breath
  [F.E5,1],[F.D5,1],[F.C5,1],[F.A4,1],  // ↓ high descend (climax)
  [F.G4,1],[F.A4,1],[F.C5,1],[F.D5,1],  // ↑ building again
  [F.E5,1],[F.C5,1],[F.G4,1],[F.A4,1],  //   playful leap
  [F.G4,4],                              //   grand hold — music ends here
];

// ── Bass — root notes, 8 beats each ─────────────────────────────────────────
const BASS = [
  [F.C3, 8],
  [F.G3, 8],
  [F.A3, 8],
  [F.G3, 8],
];

// ── Pad chords — gentle harmonic bed, slow attack/release ───────────────────
const PAD = [
  [[F.C4, F.G4], 8],
  [[F.G3, F.D4], 8],
  [[F.A3, F.E4], 8],
  [[F.G3, F.D4], 8],
];

const TOTAL_BEATS = 32;
const TOTAL_DUR   = TOTAL_BEATS * B; // exactly 24 s

// ── Schedule all notes for one playthrough ───────────────────────────────────
function scheduleAll(ctx, dest, startTime) {
  let t = startTime;

  // Melody — triangle wave: warm bell / toy-piano tone
  for (const [freq, beats] of MELODY) {
    const dur = beats * B;
    if (freq) {
      const osc = ctx.createOscillator();
      const g   = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.18, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur * 0.88);
      osc.connect(g);
      g.connect(dest);
      osc.start(t);
      osc.stop(t + dur);
    }
    t += dur;
  }

  // Bass — deep sine, gentle swell
  t = startTime;
  for (const [freq, beats] of BASS) {
    const dur = beats * B;
    const osc = ctx.createOscillator();
    const g   = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.09, t + 0.2);
    g.gain.setValueAtTime(0.09, t + dur - 0.3);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(g);
    g.connect(dest);
    osc.start(t);
    osc.stop(t + dur);
    t += dur;
  }

  // Pad — very slow attack so it wraps around the melody
  t = startTime;
  for (const [freqs, beats] of PAD) {
    const dur = beats * B;
    for (const freq of freqs) {
      const osc = ctx.createOscillator();
      const g   = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.05, t + 1.0); // slow attack
      g.gain.setValueAtTime(0.05, t + dur - 1.0);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      osc.connect(g);
      g.connect(dest);
      osc.start(t);
      osc.stop(t + dur);
    }
    t += dur;
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────────
export default function useMarketingAudio() {
  const ctxRef    = useRef(null);
  const masterRef = useRef(null);
  const startedRef= useRef(false);
  const stopTimerRef = useRef(null);

  const initAndPlay = useCallback(() => {
    if (startedRef.current) {
      ctxRef.current?.resume();
      return;
    }
    startedRef.current = true;

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    const ctx    = new AudioCtx();
    const master = ctx.createGain();
    master.gain.value = 0.7;
    master.connect(ctx.destination);
    ctxRef.current  = ctx;
    masterRef.current = master;

    scheduleAll(ctx, master, ctx.currentTime + 0.1);
  }, []);

  // Fade out music and close context — called when CTA slide appears
  const stopAudio = useCallback(() => {
    const ctx    = ctxRef.current;
    const master = masterRef.current;
    if (!ctx || !master) return;
    const now = ctx.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(master.gain.value, now);
    master.gain.linearRampToValueAtTime(0, now + 1.8); // graceful 1.8s fade
    stopTimerRef.current = setTimeout(() => {
      ctxRef.current?.close();
      ctxRef.current = null;
    }, 2500);
  }, []);

  // Mute / unmute
  const syncMute = useCallback((muted) => {
    const ctx    = ctxRef.current;
    const master = masterRef.current;
    if (!ctx || !master) return;
    const now = ctx.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(master.gain.value, now);
    master.gain.linearRampToValueAtTime(muted ? 0 : 0.7, now + 0.3);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeout(stopTimerRef.current);
      ctxRef.current?.close();
      ctxRef.current = null;
    };
  }, []);

  return { initAndPlay, stopAudio, syncMute, totalDuration: TOTAL_DUR };
}
